import { UserPlaceholder } from '@/components/ui/placeholder-media';
import { generatedAvatarFor } from '@/lib/generated-avatar';
import { cn } from '@/lib/utils';

/**
 * A person's generated avatar: their initial in ink on their colour
 * (src/lib/generated-avatar.ts), filling whatever round box it is put in. With
 * no uid to draw from it is the plain silhouette, as every avatar used to be.
 *
 * Reach it through UserAvatar or AvatarPicture (src/components/ui/user-avatar.tsx),
 * which decide when the initial stands in for a picture; this only draws.
 *
 * AN SVG, SO IT SCALES WITH ITS BOX: the same drawing is the 16px avatar on a
 * product card and the 128px one at the top of a storefront. The letter sits
 * at 46% of the box, its baseline 0.35em below the middle, which centres a
 * capital's height (Inter's caps are 0.73em tall).
 *
 * DECORATIVE. The person's name is always beside it or on the link around it,
 * so it is aria-hidden rather than read twice.
 *
 * IN THE SITE FONT, NOT A SELLER'S. `font-body` pins Inter even inside a
 * seller page that sets its own fonts, so one person looks the same on their
 * storefront and everywhere else; the spacing, weight and style are set on the
 * letter too, so a heading or an italic row around it cannot change it.
 *
 * NO HOOKS, NO BROWSER APIS AND NO CLIENT DIRECTIVE: a server component (the
 * storefront nav) can draw it straight into the HTML, and the server and the
 * browser draw the same thing from the same two strings.
 */
export function GeneratedAvatar({
  seed,
  name,
  className,
}: {
  /** Their uid, which picks the colour. */
  seed?: string | null;
  /** Their username, whose first letter is drawn. */
  name?: string | null;
  className?: string;
}) {
  const avatar = generatedAvatarFor(seed, name);
  const box = cn('block h-full w-full select-none', className);
  if (!avatar) return <UserPlaceholder aria-hidden="true" focusable="false" className={box} />;
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false" className={box}>
      <rect width="100" height="100" fill={avatar.fill} />
      {avatar.initial ? (
        <text
          x="50"
          y="50"
          dy="0.35em"
          textAnchor="middle"
          fontSize="46"
          fontWeight="600"
          fontStyle="normal"
          letterSpacing="0"
          fill={avatar.ink}
          className="font-body"
        >
          {avatar.initial}
        </text>
      ) : (
        // A name with no letter or digit: the silhouette's figure, in ink on
        // their colour.
        <>
          <circle cx="50" cy="40" r="20" fill={avatar.ink} />
          <path d="M20,80 C20,60 80,60 80,80 V100 H20 Z" fill={avatar.ink} />
        </>
      )}
    </svg>
  );
}
